import { Component, OnInit, ViewEncapsulation, ViewChild, ElementRef } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Inject } from '@angular/core';
import { Router } from '@angular/router';
import { Validators } from '@angular/forms';
import { FormGroup, FormControl } from '@angular/forms';
import { LocalStorageUtilities } from '@app/utilities/local-storage-utilities';
import { AgreementStatus } from "@app/enumerations/agreement-status";
import { AuthorizationService } from '@app/services/authorization.service';
import { AdminEmailService } from '@app/services/admin-email.service';
import { UserCredentials } from '@app/models/user-credentials';
import { SpotPrice } from '@app/models/spot-price';
import { Webauthentication } from '@app/models/webauthentication';
import { CustomerDocument } from '@app/models/customer-document';
import { LoggingService } from '@app/services/logging.service';
import { ArisMessageService } from '@app/services/aris-message.service';
import { LogEventStrings } from '@app/utilities/log-event-strings';
import { PersonPortalState } from '@app/models/person-portal-state';
import { ArisMessageCodes } from '@app/utilities/aris-message-codes';
import { HttpErrorResponse } from '@angular/common/http';
import { Mxna } from '@app/models/mxna';
import { SetupResponse } from '@app/models/setup-response';
import { EmailTemplateCodes } from '@app/utilities/email-template-codes';
import { HttpStatusCodes } from '@app/utilities/http-status-codes';
import { LogEventCodes } from '@app/utilities/log-event-codes';
import { StringUtilities } from '@app/utilities/string-utilities';
import { MatTabChangeEvent } from '@angular/material/tabs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit {

  static readonly PANEL_WIDTH_OPEN: string = "100%";
  static readonly PANEL_WIDTH_CLOSED: string = "0%";
  static readonly TITLE_TEXT: string = 'Monex Customer Portal';
  // Properties.
  public userName: string = "";
  public password: string = "";
  public notificationText: string = "test - remove when finished";
  public isFooterShowing: boolean = false;
  public isLoading: boolean = false;
  public isNotificationPanelShowing: boolean = false;
  public passwordFieldHide: boolean = true;
  public hiddenPasswordPanelPosition: string = LoginComponent.PANEL_WIDTH_CLOSED;
  public hiddenUserIdPanelPosition: string = LoginComponent.PANEL_WIDTH_CLOSED;
  public failedLoginPanelPosition: string = LoginComponent.PANEL_WIDTH_CLOSED;
  public failedSetupPanelPosition: string = LoginComponent.PANEL_WIDTH_CLOSED;
  public accountAccessDisabledPanelPosition: string = LoginComponent.PANEL_WIDTH_CLOSED;
  public temporaryPasswordExpiredPanelPosition: string = LoginComponent.PANEL_WIDTH_CLOSED;
  public actionSucceededPanelPosition: string = LoginComponent.PANEL_WIDTH_CLOSED;
  // Fields.  TODO: Check to see if these are necessary.
  public field1PlaceholderText: string = "Account Number";
  public field2PlaceholderText: string = "Last 4 Digits of SSN";
  public field3PlaceholderText: string = "Email Address";

  // Form groups.
  public loginFormGroup: FormGroup = new FormGroup({
    userIdFormControl: new FormControl('', Validators.compose([Validators.required, Validators.maxLength(45)])),
    passwordFormControl: new FormControl('', [Validators.required, Validators.maxLength(45)]),
  });

  public setupFormGroup: FormGroup = new FormGroup({
    accountFormControl: new FormControl('', Validators.compose([Validators.required, Validators.maxLength(9), Validators.minLength(9), Validators.pattern('[0-9]+')])),
    ssnFormControl: new FormControl('', Validators.compose([Validators.required, Validators.maxLength(4), Validators.minLength(4), Validators.pattern('[0-9]+')])),
    emailFormControl: new FormControl('', Validators.compose([Validators.required, Validators.maxLength(45), Validators.minLength(5), Validators.email])),
  });

  public resetPasswordFormGroup: FormGroup = new FormGroup({
    passwordResetFormControl: new FormControl('', Validators.compose([Validators.required, Validators.maxLength(45), Validators.minLength(5)])),
  });

  public forgotUserIdFormGroup: FormGroup = new FormGroup({
    forgotUserIdFormControl: new FormControl('', Validators.compose([Validators.required, Validators.minLength(9), Validators.maxLength(9)])),
  });

  constructor(
    private router: Router,
    private authorizationService: AuthorizationService,
    private loggingService: LoggingService,
    private arisMessageService: ArisMessageService,
    private adminEmailService: AdminEmailService,
    @Inject(DOCUMENT) private document: Document) { }

  ngOnInit(): void {
   
  }

   /**           
    * 2026-04-27 - Removed code that truncated commodity code before
    *              comparison is made (price.symbol === position.commodity.slice(0, 4).trim()). 
    * 
    * Invoked from login button.
   */
  public onLogin(): void {
    if (!this.loginFormGroup.invalid) {
      let userName: string = this.loginFormGroup.controls['userIdFormControl'].value;
      let password: string = this.loginFormGroup.controls['passwordFormControl'].value;
      let userCredentials: UserCredentials = new UserCredentials(userName, password);      
      this.authorizationService.isAccountAccessDisabled(userName).subscribe({
        next: (isDisabled: boolean) => {         
          if (isDisabled === false) {
            // Start spinner.
            this.showProgressPanel()            
            this.authorizationService.login(userCredentials)
              .subscribe({
                next: (webauthentication: Webauthentication) => {
                  // If authorization fails, an empty Webauthentication is returned. TODO: Probably better to return null.
                  // If acctNum is null, the user could not be authorized!                                  
                  if (webauthentication) {
                    /**
                     * Make sure the agreement is the latest version.
                     */
                    if (webauthentication.customerAgreementList) {
                      let personPortalState: PersonPortalState = LocalStorageUtilities.getPersonPortalState();
                      // Mxna
                      this.authorizationService.findMxnaById(personPortalState.acctNum).subscribe({
                        next: (mxna: Mxna) => {
                          personPortalState.mxna = mxna;                         
                          // Update current market value                
                          this.authorizationService.findDelayedPrices().subscribe({
                            next: (prices: SpotPrice[]) => {
                              personPortalState.mxna.mxPosTrade.forEach((position) => {
                                prices.forEach((price: SpotPrice) => {                                                                                                     
                                  if (price.symbol.trim() === position.commodity.trim()) {
                                    if (position.unitBal > 0) {
                                      position.currentMktVal = price.last_bid;
                                    } else {
                                      position.currentMktVal = price.last_ask;
                                    }
                                    position.extendedMktVal = position.unitBal * position.currentMktVal;
                                  }
                                });
                              })
                              this.authorizationService.findDocuments(personPortalState.co, personPortalState.acctNum).subscribe({
                                next: (customerDocuments: CustomerDocument[]) => {
                                  personPortalState.customerDocuments = customerDocuments;
                                  // Route customer to Main screen.
                                  this.routeCustomer(webauthentication, personPortalState);
                                },
                                error: (err: HttpErrorResponse) => {
                                  if (err.status == HttpStatusCodes.NOT_FOUND) {
                                    let noDocs: CustomerDocument[] = [];
                                    personPortalState.customerDocuments = noDocs;
                                    // Route customer to the Site Agreement screen.
                                    this.routeCustomer(webauthentication, personPortalState);
                                  }
                                }
                              });
                            },
                            error: (err: any) => {
                              this.onShowLoginFailedPanelAction();
                            }
                          });
                        }
                      })
                    }
                  } else {
                    // Customer not found.                      
                    this.onShowLoginFailedPanelAction();
                  }
                },
                error: (err: HttpErrorResponse) => {
                  this.hideProgressPanel()
                  // Check to see if the user's temporary password has expired (HttpStatusCodes.NOT_IMPLEMENTED).
                  if (err.status !== HttpStatusCodes.NOT_IMPLEMENTED) {
                    // Login failed. As such, increment the count in the user's webauthentication record assuming
                    // the User ID can be found.                                   
                    this.authorizationService.getAndIncrementFailedCount(userName).subscribe({
                      next: (failedLoginCount: number) => {
                        // Log login attempt faild event.
                        this.loggingService.logEvent(LogEventStrings.UNSUCCESSFUL_LOGIN, LogEventCodes.UNSUCCESSFUL_LOGIN_CODE, undefined, userName);
                      },
                      error: (err: HttpErrorResponse) => {
                        // // The User ID could not be found.
                        // this.onShowLoginFailedPanelAction();
                      }
                    })
                    this.onShowLoginFailedPanelAction();
                  } else {
                    // Show temporary password expired panel.
                    this.onShowTemporaryPasswordExpiredPanelAction();
                    // Log login attempt faild event.
                    this.loggingService.logEvent(LogEventStrings.CUSTOMER_TEMP_PASSWORD_EXPIRED, LogEventCodes.CUSTOMER_TEMP_PASSWORD_EXPIRED_CODE, undefined, userName);
                  }
                }
              });
          } else {
            this.onShowAccountAccessDisabledPanelAction();
            // Log account access disabled event.
            this.loggingService.logEvent(LogEventStrings.CUSTOMER_ACCESS_DISABLED, LogEventCodes.CUSTOMER_ACCESS_DISABLED_CODE, undefined, userName);
            // Send admin. email
            this.adminEmailService.sendAdminEmail("Admin - Customer's account access has been disabled", undefined, userName);
          }
        },
        error: (err: HttpErrorResponse) => {
          // Invoked when no User ID is found.
          console.log("No user id exists.");
          this.onShowLoginFailedPanelAction();
        }
      })

    }
  }

  /**
   * Authenticate the user's input against MICA data.
   */
  public onSetup(): void {
    this.authorizationService.setUp(this.setupFormGroup.controls['accountFormControl'].value,
      this.setupFormGroup.controls['ssnFormControl'].value,
      this.setupFormGroup.controls['emailFormControl'].value)
      .subscribe({
        next: (setupResponse: SetupResponse) => {
          let personPortalState: PersonPortalState = LocalStorageUtilities.getPersonPortalState();
          this.authorizationService.findMxnaById(personPortalState.acctNum).subscribe({
            next: (mxna: Mxna) => {
              personPortalState.mxna = mxna;
              LocalStorageUtilities.setPersonPortalState(personPortalState);
              this.router.navigate(['/agreement']);
              /**
               *  Emails
               * 
               */
              // Send customer their temporary password email message after retrieving the template.
              this.authorizationService.findEmailTemplateByEmailId(EmailTemplateCodes.ACCOUNT_ACTIVATED)
                .subscribe({
                  next: (template: any) => {
                    let accountNum = personPortalState.acctNum.slice(-4);
                    // Inject values into email template variables.
                    let html: string = StringUtilities.formatString(template.html, accountNum, mxna.mxk9.email, setupResponse.password);
                    // Email the new password message to the customer.                 
                    this.authorizationService.sendCustomerEmail(mxna.mxk9.email, template.subject, html)
                      .subscribe({
                        next: status => {
                          console.log(status.status);
                        },
                        error: (err: HttpErrorResponse) => {
                          console.log(err);
                        }
                      })
                  }
                })
              // Send admin. email
              this.adminEmailService.sendAdminEmail("Admin - User Setup Access to Account", personPortalState.acctNum);
              // Log account access disabled event.
              this.loggingService.logEvent(LogEventStrings.SUCCESSFUL_SETUP, LogEventCodes.SUCCESSFUL_SETUP_CODE, personPortalState.acctNum);
            },
            error: (err) => {
              // Update UI to tell the user they already have credentials for access and send an email to the
              // customer and the admin.
              this.onShowSetupFailedPanelAction();
            }
          });
        },
        error: (err) => {
          // Update UI to tell user that they already have access and send an email to the customer and the admin.
          this.onShowSetupFailedPanelAction();
        }
      })
  }

  /**
   * Called when the user clicks the "Forgot User ID" button.
   * 
   * Emails the user using his "account number".
   */
  public onForgotUserId(): void {
    // Get account number.
    let account: string = this.forgotUserIdFormGroup.controls['forgotUserIdFormControl'].value;
    // Send customer email.
    this.authorizationService.sendIdEmailToCustomer(account, 3).subscribe({
      next: (response: any) => {
        this.onShowActionSucceededPanelAction();
      },
      error: (err: HttpErrorResponse) => {
        console.log("Unable to email User ID to the user.");
      }
    });
    // Send admin. email
    this.adminEmailService.sendAdminEmail("Admin - User Forgot ID", account);
    // Hide panel.
    this.onHideForgotUserIdPanelAction();
  }

  /**
   * Called when the user clicks the "Forgot Password" button.
   */
  public onForgotPassword(): void {
    // User ID from the form.
    let userId: string = this.resetPasswordFormGroup.controls.passwordResetFormControl.value;
    this.authorizationService.sendPasswordEmailToCustomer(userId, 5)
      .subscribe({
        next: (response: any) => {
          this.onShowActionSucceededPanelAction();
        },
        error: (err: HttpErrorResponse) => {
          console.log("Unable to email new password to the user.");
        }
      })
    /* Send admin. email without account number. The account number will be looked up
    *  in the the REST service method.
    */
    this.adminEmailService.sendAdminEmail("Admin - User Forgot Password", undefined, userId);
    this.onHideForgotPasswordPanelAction();
  }

  /**
   * Initial login state is saved here!!!
   * 
   * @param webauthentication    
   * @param personPortalState 
   */
  private routeCustomer(webauthentication: Webauthentication, personPortalState: PersonPortalState) {
    // If the user has accepted the Use Agreement, navigate to the Main screen.                     
    if (webauthentication.customerAgreementList.length > 0) {
      LocalStorageUtilities.clearPersonPortalState();
      // Saves initial state.
      try {
        LocalStorageUtilities.setPersonPortalState(personPortalState);
      } catch (e) {
        if (e.code == 22) {
          // Storage full, maybe notify user or do some clean-up
          console.log("Storage is full.");
        }
      }
      // Save misc. info. to database.
      webauthentication.lastSuccessfulLoginDate = new Date();
      webauthentication.failedLoginCount = 0;
      this.authorizationService.updateWebAuthenticaton(webauthentication, webauthentication.id)
        .subscribe({
          next: (webauthentication: Webauthentication) => {
            // Log the customer's event.
            this.loggingService.logEvent(LogEventStrings.SUCCESSFUL_LOGIN, LogEventCodes.SUCCESSFUL_LOGIN_CODE, webauthentication.acctNum);
            /**
             * Send event to ARIS.
             */
            this.arisMessageService.send(ArisMessageCodes.CUSTOMER_LOGGED_IN, personPortalState.mxna.acctRep, personPortalState.mxna.account);
            /**
             * Send Admin. email.
             */
            // Send admin. email
            this.adminEmailService.sendAdminEmail("Admin - User has Logged In", personPortalState.acctNum);
          },
          error: (err: HttpErrorResponse) => {
          }
        });
      this.router.navigate(['/main']);
    } else {
      // The has not yet accepted the Use Agreement, navigate to the Site Agreement screen.                    
      // These values are utilized in "Authorization Guard" module.                      
      personPortalState.acceptedAgreement = AgreementStatus.DECLINED;
      LocalStorageUtilities.clearPersonPortalState();
      LocalStorageUtilities.setPersonPortalState(personPortalState);
      this.router.navigate(['/agreement']);
    }
  }

  /**
   * Forgot password panel
   */
  public onShowForgotPasswordPanelAction(): void {
    // this.setFocusOnElement("#user-id-field");
    this.hiddenPasswordPanelPosition = LoginComponent.PANEL_WIDTH_OPEN;
  }

  public onHideForgotPasswordPanelAction(): void {
    this.resetPasswordFormGroup.reset();
    this.hiddenPasswordPanelPosition = LoginComponent.PANEL_WIDTH_CLOSED;
    // this.setFocusOnElement("#landing-user-id-field");
  }

  /**
  * Forgot User ID panel
  */
  public onShowForgotUserIdPanelAction(): void {
    // this.setFocusOnElement("#forgot-user-id-panel");
    this.forgotUserIdFormGroup.reset();
    this.hiddenUserIdPanelPosition = LoginComponent.PANEL_WIDTH_OPEN;
  }

  public onHideForgotUserIdPanelAction(): void {
    // this.forgotUserIdFormGroup.reset();
    this.hiddenUserIdPanelPosition = LoginComponent.PANEL_WIDTH_CLOSED;
    //   this.setFocusOnElement("#landing-user-id-field");
  }

  /**
  * Login failed panel actions.
  */
  public onShowLoginFailedPanelAction(): void {
    this.failedLoginPanelPosition = LoginComponent.PANEL_WIDTH_OPEN;
    // Attempt to move focus to the panel's button.     
    this.setFocusOnElement("#faildLoginPanelButton");
  }

  public onHideLoginFailedPanelAction(): void {
    this.failedLoginPanelPosition = LoginComponent.PANEL_WIDTH_CLOSED;
  }

  /**
  * Setup failed
  */
  public onShowSetupFailedPanelAction(): void {
    this.failedSetupPanelPosition = LoginComponent.PANEL_WIDTH_OPEN;
  }

  public onHideSetupFailedPanelAction(): void {
    this.failedSetupPanelPosition = LoginComponent.PANEL_WIDTH_CLOSED;
  }

  /**
  * Account access disabled
  */
  public onShowAccountAccessDisabledPanelAction(): void {
    this.accountAccessDisabledPanelPosition = LoginComponent.PANEL_WIDTH_OPEN;
  }

  public onHideAccountAccessDisabledPanelAction(): void {
    this.accountAccessDisabledPanelPosition = LoginComponent.PANEL_WIDTH_CLOSED;
  }

  /**
  * Temporary password has expired.
  */
  public onShowTemporaryPasswordExpiredPanelAction(): void {
    this.temporaryPasswordExpiredPanelPosition = LoginComponent.PANEL_WIDTH_OPEN;
  }

  public onHideTemporaryPasswordExpiredPanelAction(): void {
    this.temporaryPasswordExpiredPanelPosition = LoginComponent.PANEL_WIDTH_CLOSED;
  }

  /**
  * Actions succeeded temporaryPasswordExpiredPanelPosition
  */
  public onShowActionSucceededPanelAction(): void {
    this.actionSucceededPanelPosition = LoginComponent.PANEL_WIDTH_OPEN;
  }

  public onHideActionSucceededPanelAction(): void {
    this.actionSucceededPanelPosition = LoginComponent.PANEL_WIDTH_CLOSED;
  }

  private showProgressPanel() {
    this.isLoading = true;
  }

  private hideProgressPanel() {
    this.isLoading = false;
  }

  public onTabChanged(event: MatTabChangeEvent) {
    let clickedIndex = event.index;
    switch (clickedIndex) {
      case 0: {
        this.resetForm(this.loginFormGroup);
        break;
      }
      case 1: {
        this.resetForm(this.setupFormGroup);
      }
    }
  }

  /**
  * Used primarily in conjunction with setting keyboard focus on
  * elements that are not yet visible.  This is why the standard
  * Angular approach isn't sufficient.
  * @param elementName    * 
  */
  private setFocusOnElement(elementName: string) {
    let div: HTMLElement = this.document.querySelector(elementName);
    div.focus();
  }

  /**
   *  Form control error handlers.
   */
  public landingFormHasError = (controlName: string, errorName: string) => {
    return this.loginFormGroup.controls[controlName].hasError(errorName);
  }

  public setupHasError = (controlName: string, errorName: string) => {
    return this.setupFormGroup.controls[controlName].hasError(errorName);
  }

  public forgotUserIdFormHasError = (controlName: string, errorName: string) => {
    return this.forgotUserIdFormGroup.controls[controlName].hasError(errorName);
  }

  public resetFormHasError = (controlName: string, errorName: string) => {
    return this.resetPasswordFormGroup.controls[controlName].hasError(errorName);
  }

  private resetForm(form: FormGroup): void {
    form.markAsPristine();
    form.markAsUntouched();
  }

}
